import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Card, Grid } from 'antd';
import { getColorForEntity } from '../../utils/colors';

const MaxAvailChart = ({ data, aggregationLevel, chartLevel, loading, sharedTooltip = false }) => {
  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>Loading chart...</div>;
  }

  if (!data || data.length === 0) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>No data available</div>;
  }

  // Check if data is in V2 format (multiple charts) or V1 format (single chart)
  const isMultipleCharts = chartLevel !== 'system' && Array.isArray(data) && data[0]?.series;

  if (isMultipleCharts) {
    return <MultipleCharts data={data} aggregationLevel={aggregationLevel} chartLevel={chartLevel} sharedTooltip={sharedTooltip} />;
  }

  return <SingleChart data={data} aggregationLevel={aggregationLevel} sharedTooltip={sharedTooltip} />;
};

// V1 behavior: Single chart
const SingleChart = ({ data, aggregationLevel, sharedTooltip = false }) => {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const options = {
    time: {
      timezone: 'Australia/Brisbane'
    },
    chart: {
      type: 'line',
      zoomType: 'xy',
      height: isMobile ? 300 : 500
    },
    title: {
      text: `Maximum Availability Forecast - ${aggregationLevel.toUpperCase()} Level`
    },
    xAxis: {
      type: 'datetime',
      title: { text: isMobile ? undefined : 'Interval DateTime (AEST)' },
      labels: {
        format: isMobile ? '{value:%d/%m %H:%M}' : '{value:%Y-%m-%d %H:%M}',
        ...(isMobile && { rotation: -45, style: { fontSize: '10px' } })
      }
    },
    yAxis: {
      title: { text: isMobile ? 'MW' : 'MaxAvail (MW)' },
      min: 0
    },
    series: data.map((series, index) => ({
      name: series.name,
      data: series.data,
      color: getColorForEntity(series.name, series.fuelType, index)
    })),
    legend: {
      enabled: true,
      maxHeight: isMobile ? 60 : 120,
      ...(isMobile && { itemStyle: { fontSize: '10px' } }),
      navigation: {
        enabled: true
      }
    },
    tooltip: {
      shared: sharedTooltip,
      crosshairs: true,
      valueSuffix: ' MW',
      valueDecimals: 2,
      xDateFormat: '%Y-%m-%d %H:%M'
    },
    plotOptions: {
      line: {
        lineWidth: 2,
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: true,
              radius: 4
            }
          }
        },
        states: {
          hover: {
            lineWidthPlus: 4  // Makes it 2+4=6 (3x thicker on hover)
          }
        }
      }
    },
    credits: {
      enabled: false
    }
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

// V2 behavior: Multiple charts
const MultipleCharts = ({ data, aggregationLevel, chartLevel, sharedTooltip = false }) => {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {data.map((chartData, chartIndex) => {
        const options = {
          time: {
            timezone: 'Australia/Brisbane'
          },
          chart: {
            type: 'line',
            zoomType: 'xy',
            height: isMobile ? 250 : 400
          },
          title: {
            text: chartData.chartTitle
          },
          xAxis: {
            type: 'datetime',
            title: { text: isMobile ? undefined : 'Interval DateTime (AEST)' },
            labels: {
              format: isMobile ? '{value:%d/%m %H:%M}' : '{value:%Y-%m-%d %H:%M}',
              ...(isMobile && { rotation: -45, style: { fontSize: '10px' } })
            }
          },
          yAxis: {
            title: { text: isMobile ? 'MW' : 'MaxAvail (MW)' },
            min: 0
          },
          series: chartData.series.map((series, seriesIndex) => ({
            name: series.name,
            data: series.data,
            color: getColorForEntity(series.name, series.fuelType, seriesIndex)
          })),
          legend: {
            enabled: true,
            maxHeight: isMobile ? 60 : 80,
            ...(isMobile && { itemStyle: { fontSize: '10px' } }),
            navigation: {
              enabled: true
            }
          },
          tooltip: {
            shared: sharedTooltip,
            crosshairs: true,
            valueSuffix: ' MW',
            valueDecimals: 2,
            xDateFormat: '%Y-%m-%d %H:%M'
          },
          plotOptions: {
            line: {
              lineWidth: 2,
              marker: {
                enabled: false,
                states: {
                  hover: {
                    enabled: true,
                    radius: 4
                  }
                }
              },
              states: {
                hover: {
                  lineWidthPlus: 4  // Makes it 2+4=6 (3x thicker on hover)
                }
              }
            }
          },
          credits: {
            enabled: false
          }
        };

        return (
          <Card key={chartData.chartKey}>
            <HighchartsReact highcharts={Highcharts} options={options} />
          </Card>
        );
      })}
    </div>
  );
};

export default MaxAvailChart;
